<!-- 我的个人资料弹窗 -->
<script lang="ts" setup>
import { updateInfo } from '~/apis/auth'
import { universalUpload } from '~/apis/app'
import { useAuthStore } from '~/stores/authStore'
import { useToastHandler } from '~/composables/useToastHandler'
import Loading from '~/assets/icons/loading.svg?component'

const myProfileStore = useMyProfileStore()
const loginStore = useLoginStore()
const authStore = useAuthStore()
const { showErrorToast, showSuccessToast } = useToastHandler()

// 本地编辑状态（从 authStore 同步）
const currentAvatarUrl = ref('')
const currentDisplayName = ref('')
const accountEmail = computed(() => authStore.user?.email || '')

// 从 authStore 同步数据到本地编辑状态
const syncDataFromAuthStore = () => {
  const user = authStore.user
  if (user) {
    currentAvatarUrl.value = user.avatar || ''
    currentDisplayName.value = user.nickname || user.username || user.email || ''
  } else {
    currentAvatarUrl.value = ''
    currentDisplayName.value = ''
  }
}

// 监听弹窗打开，同步数据
watch(() => myProfileStore.isOpenMyProfileModal, (isOpen) => {
  if (isOpen) {
    syncDataFromAuthStore()
  }
})

// 监听 authStore.user 变化，同步数据
watch(() => authStore.user, () => {
  if (myProfileStore.isOpenMyProfileModal) {
    syncDataFromAuthStore()
  }
}, { immediate: true })

const isUploading = ref(false)
const isSaving = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

const handleResetPassword = () => {
  myProfileStore.closeMyProfileModal()
  loginStore.openLoginModal('ForgetPasswordSignUpVerifyCode', { fromMyProfile: true })
}

const handleReplaceAvatar = () => {
  fileInputRef.value?.click()
}

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) {
    return
  }

  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    showErrorToast('Please select an image file')
    return
  }

  // 验证文件大小（例如：5MB）
  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    showErrorToast('Image size should be less than 5MB')
    return
  }

  try {
    isUploading.value = true

    // 调用上传接口
    const response = await universalUpload({
      sex: '1',
      resize: '1',
      fileType: 'image',
      accelerate: '1'
    }, file)

    // 获取返回的图片地址
    if (response.success && response.data) {
      currentAvatarUrl.value = response.data as string
    } else {
      showErrorToast(response.message || 'Upload failed')
    }
  } catch (error: any) {
    showErrorToast(error, 'Upload failed, please try again')
  } finally {
    isUploading.value = false
    // 重置文件输入
    if (target) {
      target.value = ''
    }
  }
}

const handleSave = async () => {
  if (!currentDisplayName.value.trim()) {
    showErrorToast('Please enter a display name')
    return
  }

  try {
    isSaving.value = true

    // 调用更新接口
    const response = await updateInfo({
      avatar: currentAvatarUrl.value,
      nickname: currentDisplayName.value.trim()
    })

    if (response.success) {
      // 更新成功后，刷新用户信息
      await authStore.fetchUserProfile()
      showSuccessToast('Profile updated successfully')
      myProfileStore.closeMyProfileModal()
    } else {
      showErrorToast(response.message || 'Update failed')
    }
  } catch (error: any) {
    showErrorToast(error || 'Update failed, please try again')
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <UModal
    v-model:open="myProfileStore.isOpenMyProfileModal"
    :ui="{
      overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
      content: 'w-[calc(100%-32px)] md:w-[658px] bg-[#2D2D39] rounded-[16px] ring-[none]'
    }"
  >
    <template #content>
      <!-- 内层背景内容 -->
      <div class="relative w-full h-full font-['Roboto'] p-[32px]">
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[10px] right-[10px] flex items-center justify-center w-[34px] h-[34px] cursor-pointer before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] before:rotate-45 before:rounded-[20px] after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] after:-rotate-45 after:rounded-[20px]"
          @click="myProfileStore.closeMyProfileModal()"
        />

        <!-- 标题 -->
        <p class="text-[22px] leading-[28px] text-[#FFFFFF] text-center mb-[18px] md:mb-[32px]">
          My Profile
        </p>

        <!-- 主体内容 -->
        <div class="flex flex-col md:flex-row gap-[16px]">
          <!-- 左侧头像 -->
          <div class="relative w-[200px] h-[200px] rounded-[8px] mx-auto overflow-hidden mb-[16px] md:mb-0">
            <img
              :src="currentAvatarUrl || 'https://via.placeholder.com/200'"
              alt="avatar"
              class="w-full h-full object-cover"
            >
            <!-- 上传加载遮罩 -->
            <div
              v-if="isUploading"
              class="absolute inset-0 bg-[#0F0F18]/60 flex items-center justify-center"
            >
              <Loading class="size-[24px] text-[#E7E7EC] animate-spin" />
            </div>
            <!-- 替换按钮 -->
            <button
              type="button"
              :disabled="isUploading"
              class="absolute bottom-[10px] right-[10px] flex items-center gap-x-[4px] py-[2px] px-[4px] bg-[#1313188F] text-[12px] text-[#E7E7EC99] rounded-[4px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              @click="handleReplaceAvatar"
            >
              <UIcon
                name="gridicons:share-ios"
                class="text-[16px]"
              />
              <span>{{ isUploading ? 'Uploading...' : 'Replace' }}</span>
            </button>
            <!-- 隐藏的文件输入框 -->
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleFileChange"
            >
          </div>

          <!-- 右侧表单 -->
          <div class="flex-1 flex flex-col gap-y-[24px]">
            <!-- Display Name -->
            <div class="flex flex-col gap-[8px]">
              <p class="text-[14px] text-[#B8B8C7] leading-[20px]">
                Display Name
              </p>
              <UInput
                v-model="currentDisplayName"
                class="w-full"
                :ui="{
                  base: 'w-full text-[16px] md:text-[14px] text-[#FFFFFF] leading-[20px] bg-[#262635] rounded-[8px] border border-[#5E5E77] py-[10px] px-[12px] ring-[none] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#5E5E77] focus-visible:!ring-[none]'
                }"
              />
            </div>

            <!-- Account -->
            <div class="flex flex-col gap-[4px]">
              <p class="text-[14px] text-[#B8B8C7] leading-[20px]">
                Account
              </p>
              <div class="text-[14px] text-[#B8B8C7] leading-[20px]">
                {{ accountEmail }}
              </div>
            </div>

            <!-- Reset Password -->
            <button
              type="button"
              class="flex items-center justify-between w-full px-[12px] py-[10px] rounded-[8px] bg-[#383847] cursor-pointer"
              @click="handleResetPassword()"
            >
              <span class="text-[14px] text-[#E7E7EC] leading-[20px]">
                Reset Password
              </span>
              <span class="text-[16px] text-[#B8B8C7]">
                ❯
              </span>
            </button>

            <!-- 底部保存按钮 -->
            <div class="flex justify-end mt-[8px]">
              <UButton
                class="flex justify-center py-[12px] px-[34px] bg-[#BF2AFF] hover:bg-[#590080] active:bg-[#590080] disabled:bg-[#590080] disabled:opacity-50 rounded-[8px] border-none cursor-pointer disabled:cursor-not-allowed"
                type="submit"
                :disabled="isSaving || isUploading"
                :ui="{
                  label: 'text-[14px] text-white leading-[16px]'
                }"
                :label="isSaving ? 'Saving...' : 'Save'"
                @click="handleSave"
              >
                <template
                  v-if="isSaving"
                  #leading
                >
                  <Loading
                    v-if="isSaving"
                    class="size-[16px] text-[#E7E7EC] animate-spin"
                  />
                </template>
              </UButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
